import { CRUDMutations } from '@/utils/store-utilities'

/**
 * Default factory for review summary entity
 * @param {Object} item - Raw entity data
 * @returns {Object} Normalized entity
 */
const defaultItem = (item) => ({
  id: null,
  product_id: null,
  language: 'en',
  summary: '',
  aspects: [],
  reviews_count: 0,
  average_score: null,
  status: 0,
  is_visible: true,
  is_locked: false,
  ai_request_log_id: null,
  model: '',
  error_message: null,
  generated_at: null,
  ...item,
})

export const state = () => ({
  items: [],
  item: null,
  stats: null,
})

export const mutations = {
  ...CRUDMutations(defaultItem),
  setItem(state, item) {
    state.item = item
  },
  setStats(state, stats) {
    state.stats = stats
  },
}

export const getters = {
  list(state) {
    return state.items
  },
  current(state) {
    return state.item
  },
  stats(state) {
    return state.stats
  },
}

export const actions = {
  /**
   * Fetches paginated list of review summaries
   * @param {Object} context - Vuex context
   * @param {Object} params - Query params
   * @returns {Promise<Object>} API response
   */
  async fetchSummaries({ commit }, params = {}) {
    const response = await this.$axios.$get('admin/reviewSummaries', { params })
    if (response && response.data) {
      commit('load', response.data.map(defaultItem))
    } else {
      commit('clear')
    }
    return response
  },

  /**
   * Fetches single review summary by ID
   * @param {Object} context - Vuex context
   * @param {number|string} id - Summary ID
   * @returns {Promise<Object>} Summary data
   */
  async fetchSummary({ commit }, id) {
    const response = await this.$axios.$get(`admin/reviewSummaries/${id}`)
    if (response && response.data) {
      commit('setItem', defaultItem(response.data))
      return response.data
    }
  },

  /**
   * Updates an existing review summary
   * @param {Object} context - Vuex context
   * @param {Object} payload - { id, payload }
   * @returns {Promise<Object>} API response
   */
  async updateSummary({ commit }, { id, payload }) {
    const response = await this.$axios.$put(`admin/reviewSummaries/${id}`, payload)
    if (response && response.data) {
      commit('update', defaultItem(response.data))
      commit('setItem', defaultItem(response.data))
    }
    return response
  },

  /**
   * Deletes a review summary
   * @param {Object} context - Vuex context
   * @param {number|string} id - Summary ID
   * @returns {Promise<Object>} API response
   */
  async removeSummary({ commit }, id) {
    const response = await this.$axios.$delete(`admin/reviewSummaries/${id}`)
    if (response && response.data) {
      commit('remove', { id })
    }
    return response
  },

  /**
   * Triggers regeneration for a specific review summary
   * @param {Object} context - Vuex context
   * @param {number|string} id - Summary ID
   * @returns {Promise<Object>} API response
   */
  async regenerateSummary({ commit }, id) {
    const response = await this.$axios.$post(`admin/reviewSummaries/${id}/regenerate`)
    if (response && response.data) {
      commit('update', defaultItem(response.data))
    }
    return response
  },

  /**
   * Triggers the review summary generator job immediately
   * @returns {Promise<Object>} API response
   */
  async runJobNow() {
    return await this.$axios.$post('admin/reviewSummaries/run')
  },

  /**
   * Fetches review summary overview statistics
   * @param {Object} context - Vuex context
   * @returns {Promise<Object>} API response
   */
  async fetchStats({ commit }) {
    const response = await this.$axios.$get('admin/reviewSummaries/stats')
    if (response && response.data) {
      commit('setStats', response.data)
    }
    return response
  },
}
